import {
  loadImageToCanvas,
  setBase64Gif,
} from "../../sidebar/campaigns/custom-images.js";
import { getUser, isUrl, showToast, wrappedPost } from "../utils.js";
import {
  addAttachmentsButton,
  addCodeButton,
  addCustomButton,
  addCustomVariablesButton,
  addDirectionalityButton,
  addEmojiButton,
  addImageGifButton,
  addAiVariablesRefreshButton,
  addTemplatesButton,
  addVideoButton,
} from "./tinymce-buttons.js";

const provider = localStorage.getItem("provider");
const fontfamily =
  provider === "gcp" ? "Arial, sans-serif" : "Calibri, sans-serif";
let fontsize =
  getUser().defaultFontSize || (provider === "gcp" ? "10pt" : "11pt");

const isCampaignPage = window.location.pathname === "/campaign";

const uploadAndDisplayImage = (id, file, base64, blobCache) => {
  $("#LoadingDiv").removeClass("invisible");
  const blobInfo = blobCache.create(id, file, base64);
  blobCache.add(blobInfo);
  const blob = blobInfo.blob();
  const postData = {
    data: `data:${blob.type};base64,${blobInfo.base64()}`,
    filename: blob.name,
    isImageUpload: true,
  };

  const imageObj = new Image();
  imageObj.src = postData.data;
  imageObj.onload = () => {
    wrappedPost({
      url: "/api/attachments/save",
      data: JSON.stringify(postData),
      success: (res) => {
        tinymce.activeEditor.execCommand(
          "mceInsertContent",
          false,
          `<img width="${imageObj.width > 500 ? "500" : ""}" src="${
            res.url
          }"></img>`,
        );
        $("#LoadingDiv").addClass("invisible");
      },
      error: () => $("#LoadingDiv").addClass("invisible"),
    });
  };
};

export const getAttachmentIdsFromDom = (stepId) =>
  $(`${stepId || ""} .attachments-container .attachment-wrapper`)
    .map((_, attachmentDiv) =>
      $(attachmentDiv).attr("data-id").replace("attachment-", ""),
    )
    .get();

const highlightTextCss = `
    .highlight-text {
      background-color: rgba(231, 74, 59, 0.2);
    }
  `;

const removeDisallowedHtmlTags = (str) => {
  const allowedArray = ["a", "p", "br", "br", "img", "li"];
  const matches = str.match(/(<\/?[\S][^>]*>)/gi) || [];

  matches.forEach((match) => {
    const allowed = allowedArray.some((allowedTag) =>
      ["<", "</", "< "].some((prefix) =>
        match.toLowerCase().startsWith(`${prefix}${allowedTag}`),
      ),
    );

    if (!allowed) str = str.split(match).join("");
  });

  return str;
};

export const allowSomeCommandsToEditCustomVars = (editor) => {
  editor.on("BeforeExecCommand", (e) => {
    const textFormatCommands = ["FontSize", "mceApplyTextcolor"];
    if (textFormatCommands.indexOf(e.command) !== -1) {
      $(editor.getBody()).find(".custom-var").attr("contenteditable", null);
    }
  });
  editor.on("ExecCommand", () => {
    $(editor.getBody()).find(".custom-var").attr("contenteditable", false);
  });
};

export const createEditor = ({
  selector,
  content,
  options = {},
  keyupCallback = null,
}) => {
  const customButton = options.customButton ? "customButton" : "";

  const { hideAiVariables = false } = options;
  const customVariables = options.customVariables
    ? {
        aiVariables: !hideAiVariables,
        customVariables: true,
        dateVariables: true,
        spintax: true,
        senderCustomVariables: true,
        commentVariables: true,
      }
    : null;
  const customVariablesBtn = customVariables ? "customVariablesBtn" : "";
  const emojiBtn = options.emojiPicker ? "emojiBtn" : "";
  const directionalityBtn = options.directionality ? "directionalityBtn" : "";
  const aiVariableRefreshBtn = options.aiVariablesTemplateId
    ? "aiVariableRefreshBtn"
    : "";

  let templating = "";
  if (options.code) templating += " | codeBtn";
  if (options.templateId) templating += " templatesBtn";

  let toolbar;
  if (options.GPTArrangement) {
    toolbar = [
      `${customVariablesBtn} bold italic underline | ${directionalityBtn} align numlist bullist`,
    ];
  } else if (options.todoArrangement) {
    toolbar = [
      `${customVariablesBtn} fontfamily | bold italic underline forecolor | ${directionalityBtn} align numlist bullist table`,
      `fontsizeselect | attachmentsBtn link imageGifBtn videoBtn ${emojiBtn} ${templating} ${customButton} ${aiVariableRefreshBtn}`,
    ];
  } else if (options.hideToolbar) {
    toolbar = false;
  } else {
    toolbar = [
      `${customVariablesBtn} fontfamily fontsizeselect | bold italic underline forecolor | ${directionalityBtn} align numlist bullist table`,
      `attachmentsBtn link imageGifBtn videoBtn ${emojiBtn} ${templating} ${customButton} ${aiVariableRefreshBtn}`,
    ];
  }

  let plugins = "lists link image table paste wordcount directionality";
  if (options.code) plugins += " code";

  if (options.fontsize) fontsize = options.fontsize;

  const forcedAttrs = {
    style: `margin: 0cm 0cm 0.0001pt; font-family: ${fontfamily}; font-size: ${fontsize};`,
  };

  const listAttr = {
    style: `font-family: ${fontfamily}; font-size: ${fontsize};`,
  };

  const opts = {
    placeholder: options.placeholder,
    selector,
    plugins,
    allow_conditional_comments: true,
    content_css: options.content_css || null,
    browser_spellcheck: true,
    default_link_target: "_blank",
    image_description: false,
    link_assume_external_targets: "https",
    link_context_toolbar: true,
    link_title: false,
    target_list: false,
    contextmenu: false,
    resize: false,
    toolbar_location: "bottom",
    fontsize_formats: "7.5pt 8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 24pt 36pt",
    paste_preprocess: (_, args) => {
      args.content = removeDisallowedHtmlTags(args.content); // strip HTML (other than whitelisted tags)

      const url = args.content;
      if (!isUrl(url)) return;
      $.post({
        url: "/api/campaigns/embed_media",
        data: JSON.stringify({ url }),
        success: (res) => {
          const ed = tinymce.get(selector.substring(1));
          ed.setContent(ed.getContent().replace(url, res));
        },
      });
    },
    paste_postprocess: (_, args) =>
      $(args.node)
        .find("a, p, li")
        .each((__, el) => $(el).attr($(el).is("li") ? listAttr : forcedAttrs)),
    toolbar,
    toolbar_groups: {
      align: {
        icon: "align-justify",
        tooltip: "Alignment",
        items: "alignleft aligncenter alignright",
      },
    },
    menubar: false,
    branding: false,
    statusbar: !!options.showStatusBar,
    height: options.height || 400,
    init_instance_callback: (editor) => {
      $(editor.getContainer()).find("button.tox-statusbar__wordcount").click();
      // move last aux helper to dialog, if field is in a dialog https://github.com/tinymce/tinymce/issues/5525. This is for the Variables dropdown in ReviewStep of AI Campaigns.
      const dialogContainer = editor.editorContainer.closest("dialog");
      if (dialogContainer) {
        const auxElements = document.querySelectorAll(
          "body > .tox-tinymce-aux",
        );
        if (auxElements.length)
          dialogContainer.append(auxElements[auxElements.length - 1]);
      }
    },
    relative_urls: false,
    remove_script_host: false,
    forced_root_block_attrs: forcedAttrs,
    table_default_attributes: {
      style: `border-collapse: collapse; width:100%; font-family: ${fontfamily}; font-size: ${fontsize};`,
      border: 1,
    },
    setup: (editor) => {
      const { aiVariablesTemplateId, templateId } = options;

      const addBtnArg = {
        editor,
        hideAiVariables: options.hideAiVariables,
        aiVariablesTemplateId,
        getUser,
        templateId,
        getAttachmentIds: () => getAttachmentIdsFromDom(`#tab-${templateId}`),
        customVariables,
      };

      editor.on("init", (e) => {
        const statusbar = $(e.target.editorContainer).find(".tox-statusbar");
        const sidebarWrap = $(e.target.editorContainer).find(
          ".tox-sidebar-wrap",
        );

        statusbar.detach().insertAfter(sidebarWrap);

        if (content.length) editor.setContent(content);

        $(editor.getDoc()).children().find("head").append(`
            <style type="text/css">
              ${highlightTextCss}
              ::-webkit-scrollbar {
                -webkit-appearance: none;
              }
              ::-webkit-scrollbar-thumb {
                background-color: rgba(0, 0, 0, 0.25);
                border: 4px solid transparent;
                border-radius: 12px;
                background-clip: content-box;
              }
            </style>`);

        $(".tox-toolbar-overlord button")
          .tooltip("dispose")
          .tooltip({ html: true, target: "hover" });
      });

      if (keyupCallback) {
        editor.on("keyup", () => keyupCallback(editor));
      }

      if (options.onFocus != null) {
        editor.on("focus", () => {
          options.onFocus();
        });
      }

      if (options.onChangeCallback != null) {
        editor.on("change", () => {
          options.onChangeCallback(editor);
        });
      }

      if (options.customVariables) {
        addCustomVariablesButton(addBtnArg);
      }

      if (options.customButton) {
        addCustomButton(addBtnArg);
      }

      if (options.emojiPicker) {
        addEmojiButton(addBtnArg);
      }

      if (options.directionality) {
        addDirectionalityButton(addBtnArg);
      }

      addAttachmentsButton(addBtnArg);
      addImageGifButton(addBtnArg);
      addVideoButton(addBtnArg);

      if (options.code) {
        addCodeButton(addBtnArg);
      }

      if (aiVariablesTemplateId) {
        addAiVariablesRefreshButton(addBtnArg);
      }

      if (templateId) {
        addTemplatesButton(addBtnArg);
      }
      allowSomeCommandsToEditCustomVars(editor);
    },
  };

  return tinymce.init(opts);
};

export const createHiddenEditor = (selector, content, opts) => {
  const validElements = opts.noHtml
    ? "span[class]"
    : "span[style|contentEditable=false|class]";
  const validStyles = opts.noHtml ? {} : { "*": "color" };

  return tinymce.init({
    selector,
    menubar: false,
    inline: true,
    plugins: "paste",
    paste_as_text: true,
    toolbar: false,
    valid_elements: validElements,
    valid_styles: validStyles,
    powerpaste_word_import: "clean",
    powerpaste_html_import: "clean",
    setup: (editor) => {
      editor.on("init", () => {
        if (content.length) editor.setContent(content);
        if (opts.initCallback) opts.initCallback();

        $(editor.getDoc()).children().find("head").append(`
            <style type="text/css">
              ${highlightTextCss}
            </style>`);

        $('.tox-toolbar button, .tox-toolbar [role="button"]').attr(
          "data-toggle",
          "tooltip",
        );

        $('[data-toggle="tooltip"]')
          .tooltip("dispose")
          .tooltip({ html: true, target: "hover" });
      });
      editor.on("keydown", (evt) => {
        if (evt.keyCode === 13 && evt.shiftKey) {
          evt.preventDefault();
          evt.stopPropagation();
          return false;
        }
        return true;
      });

      if (opts.keyupCallback) {
        editor.on("keyup", () => opts.keyupCallback(editor));
      }
    },
  });
};

export const tinymceOnBeforeUnload = () =>
  tinymce.editors.some((x) => !x.isNotDirty) || null;

export const initializeTinymce = () => {
  $(document).on("click", ".insert-directly", (e) => {
    const gifImage = e.target.parentElement.parentElement.firstElementChild;
    const src = gifImage.getAttribute("data-src-large");
    const width = gifImage.getAttribute("data-src-width");
    const height = gifImage.getAttribute("data-src-height");
    tinymce.activeEditor.execCommand(
      "mceInsertContent",
      false,
      `<img height="${height}" width="${width}" src="${src}" />`,
    );
    $("#insertImageGifModal").modal("hide");
  });

  $(document).on("click", ".customize-image", (e) => {
    $("#gifContainer").addClass("disabled");

    const gifImage = e.target.parentElement.parentElement.firstElementChild;
    const src = gifImage.attributes["data-src-large"].textContent;

    const image = new Image();

    fetch(src)
      .then((res) => res.arrayBuffer())
      .then((buf) => {
        const gif = new Uint8Array(buf);
        const gif64 = btoa(
          new Uint8Array(gif).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            "",
          ),
        );
        setBase64Gif(gif64);

        image.src = `data:image/gif;base64,${gif64}`;
        $("#loadingGiphy").addClass("hidden");
        $("#gifContainer").removeClass("disabled");
        $("#insertImageGifModal").modal("hide");
      });
    loadImageToCanvas({ file: "gif", image });
  });

  $("body").append(`
    <div class="modal fade" role="dialog" tabindex="-1" id="insertImageGifModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Insert/edit image/GIF ${
            isCampaignPage ? "& customize" : ""
          }</h5>
          <button
            type="button"
            class="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true"
              ><i class="fa-regular fa-xmark fa-sm"></i
            ></span>
          </button>
        </div>
        <div class="modal-body">
          <div class="align-items-center mt-1">
            <ul class="nav nav-tabs mb-3" role="tablist" id="nav-tabs">
              <li id="uploadTab" class="nav-item" role="presentation">
                <a class="nav-link active" href="#upload" data-toggle="tab"
                  ><i class="fa-solid fa-cloud-arrow-up mr-1"></i>Upload</a
                >
              </li>
              <li id="searchGiphyTab" class="nav-item" role="presentation">
                <a class="nav-link" href="#searchGiphy" data-toggle="tab"
                  ><i class="fa-solid fa-magnifying-glass mr-1"></i>Search
                  Giphy</a
                >
              </li>
              <li id="recordWebcamTab" class="nav-item" role="presentation">
                <a class="nav-link" href="#recordWebcam" data-toggle="tab"
                  ><i class="fa-solid fa-camera-web mr-1"></i>Record via Webcam</a
                >
              </li>
            </ul>
          </div>
          <div class="tab-content">
            <div id="upload" class="tab-pane active" role="tabpanel">
              <div class="dashed_upload d-flex justify-content-center m-3">
                <div class="wrapper">
                  <div class="drop" id="dragdropArea">
                    <div class="cont">
                      <i class="fas fa-cloud-upload-alt"></i>
                      <h5 class="mt-2">Drag & Drop an Image / GIF</h5>
                      <div>or</div>
                      <div class="browse">Browse...</div>
                    </div>
                    <output id="list"></output>
                    <input
                      id="fileUpload"
                      type="file"
                      accept=".png, .jpg, .gif"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div id="searchGiphy" class="tab-pane" role="tabpanel">
              <form id="searchContainer">
                <div id="searchBox">
                  <i class="far fa-magnifying-glass" aria-hidden="true"></i
                  ><input
                    id="searchGiphyInput"
                    type="search"
                    class="search-box form-control form-control-sm"
                    placeholder="Search for a GIF"
                  />
                </div>
                <button id="searchSubmit" class="btn btn-blue ml-2" type="submit">
                  Submit
                </button>
              </form>
              <p id="loadingGiphy" class="my-2 hidden">Loading...</p>
              <div id="gifContainer" class="mt-2 hidden"></div>
              <div class="w-100 text-center">
                <img src="/assets/img/attributes/giphy.jpg" class="w-25 mt-4" />
              </div>
            </div>
            <div id="recordWebcam" class="tab-pane" role="tabpanel">
              <div class="row">
                <div class="col">
                  <video
                    autoplay="true"
                    id="gifVideoElement"
                    style="width: 100%"
                  ></video>
                </div>
                <div class="col" style="display: none" id="gifRecordOutputDiv">
                  <div id="gifVideoOutput"></div>
                </div>
              </div>
              <div
                class="progress"
                id="gifRecordProgressBarWrapper"
                style="display: none"
              >
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  aria-valuenow="0"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  style="width: 0%"
                  id="gifRecordProgressBar"
                ></div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-blue" id="gifRecordBtn" type="button">
                  <i class="fas fa-video mr-2"></i
                  ><span id="gifRecordBtnText">Record</span>
                </button>
                <button
                  class="btn btn-blue"
                  id="gifGoBtn"
                  type="button"
                  style="display: none"
                >
                  Go!
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
    `);

  $("#gifContainer").html(
    [...Array(10).keys()].map(
      (i) =>
        `<div class="gif-item">
          <div class="img-container">
            <img id="gifHtmlPanel${i}" class="giphy-gif__image">
            <div class="giphy-buttons">
                <button style="display:${
                  isCampaignPage ? "block" : "none"
                }" id="customize${i}" class="enabled-giphy customize-image">Customize GIF</button>
                <button class="insert-directly">Insert </button>
            </div>
          </div></div>`,
    ),
  );

  $("#searchContainer").on("submit", (e) => {
    e.preventDefault();
    const gifContainer = $("#gifContainer");
    gifContainer.removeClass("hidden").addClass("disabled");
    $("#loadingGiphy").removeClass("hidden");

    const q = $("#searchGiphyInput").val();
    const key = "3AlNU6uJyqynHE108xDnERjIyUk9dMfh";
    const limit = 10;

    let imageCount = 0;
    const totalImages = $(".giphy-gif__image").length;
    gifContainer.toggle(!!q.length);
    if (!q.length) $("#loadingGiphy").addClass("hidden");

    $.get({
      url: `https://api.giphy.com/v1/gifs/search?q=${q}&api_key=${key}&limit=${limit}`,
      success: (res) => {
        res.data.forEach((result, ind) => {
          const fileSizeMB = (
            result.images.downsized_large.size /
            (1024 * 1024)
          ).toFixed(2);

          $(".img-container").addClass("show-giphy-images");

          if (fileSizeMB > 5) {
            $(`#customize${ind}`)
              .removeClass("enabled-giphy")
              .addClass("disabled-giphy")
              .text("Too large to customize")
              .prop("disabled", true);
          } else {
            $(`#customize${ind}`)
              .removeClass("disabled-giphy")
              .addClass("enabled-giphy")
              .text("Customize GIF")
              .prop("disabled", false);
          }

          $(`#gifHtmlPanel${ind}`)
            .prop("src", result.images.downsized.url)
            .attr("data-src-large", result.images.downsized_large.url)
            .attr("data-src-width", result.images.downsized_large.width)
            .attr("data-src-height", result.images.downsized_large.height);
        });

        $(".giphy-gif__image").on("load", () => {
          imageCount += 1;
          if (imageCount === totalImages) {
            $("#loadingGiphy").addClass("hidden");
            gifContainer.removeClass("disabled");
          }
        });
      },
    });
  });

  $("#recordWebcamTab").click(() => {
    if (navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          const { width, height } = stream.getTracks()[0].getSettings();
          const gifWidth = Math.min(width, height);
          const gifHeight = (gifWidth / width) * height;

          $("#gifRecordBtn")
            .off("click")
            .click(() => {
              const interval = 0.1;
              const numFrames = 20;
              const total = numFrames * interval * 1000;
              const diff = 25;

              const setGifProgress = (prop) => {
                $("#gifRecordProgressBarWrapper").show();
                const pcg = Math.floor((prop / total) * 100);
                $("#gifRecordProgressBar")
                  .attr("aria-valuenow", pcg)
                  .css("width", `${Number(pcg)}%`);
                if (pcg >= 100) return;

                setTimeout(() => {
                  setGifProgress(prop + diff);
                }, diff);
              };

              setGifProgress(0);

              $("#gifRecordOutputDiv, #gifGoBtn, #gifRecordBtn").hide();

              gifshot.createGIF(
                {
                  gifWidth,
                  gifHeight,
                  interval,
                  numFrames,
                  frameDuration: 1,
                  sampleInterval: 10,
                  numWorkers: 2,
                },
                (obj) => {
                  if (!obj.error) {
                    $("#gifRecordProgressBarWrapper").hide();
                    const { image } = obj;
                    const img = document.createElement("img");
                    img.src = image;
                    img.style.width = "100%";
                    $("#gifVideoOutput").empty().append(img);
                    $("#gifRecordOutputDiv, #gifGoBtn, #gifRecordBtn").show();
                    $("#gifRecordBtn")
                      .removeClass("btn-blue")
                      .addClass("btn-primary");
                    $("#gifRecordBtnText").text("Retake");

                    $("#gifGoBtn")
                      .off("click")
                      .click(() => {
                        if (isCampaignPage) {
                          const imageElement = new Image();
                          imageElement.src = image;
                          setBase64Gif(
                            image.split("data:image/gif;base64,")[1],
                          );
                          loadImageToCanvas({
                            file: "gif",
                            image: imageElement,
                          });
                        } else {
                          const id = `blobid${new Date().getTime()}`;
                          const { blobCache } =
                            tinymce.activeEditor.editorUpload;
                          const base64 = image.split(
                            "data:image/gif;base64,",
                          )[1];

                          fetch(`data:image/gif;base64,${base64}`)
                            .then((response) => response.blob())
                            .then((blob) => {
                              const file = new File([blob], `${id}.gif`, {
                                type: "image/gif",
                              });
                              uploadAndDisplayImage(
                                id,
                                file,
                                base64,
                                blobCache,
                              );
                            });
                        }
                        $("#insertImageGifModal").modal("hide");
                      });
                  }
                },
              );
            });

          const video = document.querySelector("#gifVideoElement");
          video.srcObject = stream;
        })
        .catch(() => {
          showToast({
            title: "Please give access to your camera to enable this feature",
            type: "warning",
          });
        });
    }
  });

  $("#fileUpload").change(() => {
    const file = $("#fileUpload").prop("files")[0];
    const acceptedFileTypes = ["image/png", "image/jpeg", "image/gif"];
    if (!acceptedFileTypes.includes(file.type)) {
      showToast({
        title: "Unsupported filetype",
        subtitle: "Please try again with JPG / PNG / GIF",
        type: "danger",
      });
      return;
    }
    const reader = new FileReader();

    const processImage = (imageFile, image) => {
      if (isCampaignPage) loadImageToCanvas({ file: imageFile, image });
      else {
        const id = `blobid${new Date().getTime()}`;
        const { blobCache } = tinymce.activeEditor.editorUpload;
        const base64 = image.src.split(",")[1];
        uploadAndDisplayImage(id, imageFile, base64, blobCache);
      }
    };

    const resizeImage = (originalImage, maxW = 1200, maxH = 1200) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      let { width, height } = originalImage;

      if (width > height) {
        if (width > maxW) {
          height *= maxW / width;
          width = maxW;
        }
      } else if (height > maxH) {
        width *= maxH / height;
        height = maxH;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(originalImage, 0, 0, width, height);

      return canvas.toDataURL("image/jpeg", 0.9);
    };

    reader.onload = (readerEvent) => {
      if (file.type === "image/gif") {
        const image = new Image();
        image.src = readerEvent.target.result;
        if (isCampaignPage)
          setBase64Gif(readerEvent.target.result.split(",")[1]);
        processImage(file, image);
      } else {
        const originalImage = new Image();
        originalImage.onload = () => {
          const compressedDataURL = resizeImage(originalImage);
          const compressedImg = new Image();
          compressedImg.src = compressedDataURL;
          processImage(file, compressedImg);
        };
        originalImage.src = readerEvent.target.result;
      }
    };
    reader.readAsDataURL(file);
    $("#fileUpload").val("");
    $("#insertImageGifModal").modal("hide");
  });

  $("#gifRecordModal").on("hide.bs.modal", () => {
    $("#gifRecordBtn").removeClass("btn-primary").addClass("btn-blue");
    $("#gifRecordBtnText").text("Record");
    $("#gifGoBtn").hide();
    $("#gifRecordOutputDiv").hide();
    $("#gifVideoOutput").empty();
  });

  window.onbeforeunload = tinymceOnBeforeUnload;
};
